import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error in query params
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      // Check for PKCE code flow (query param)
      const code = searchParams.get('code');
      if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          setError(error.message);
          return;
        }
        // If this is a recovery/invite flow, redirect to set password
        // Supabase sets the session type in the user's aal (or we check via event)
        const type = searchParams.get('type');
        if (type === 'recovery' || type === 'invite') {
          navigate('/auth/set-password', { replace: true });
        } else {
          navigate('/crm', { replace: true });
        }
        return;
      }

      // For hash fragment flow (#access_token=...), Supabase client with
      // detectSessionInUrl: true should auto-handle the session.
      // Check if we have a hash fragment with access_token
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const hashType = hashParams.get('type');
      const hashError = hashParams.get('error');
      const hashErrorDescription = hashParams.get('error_description');

      if (hashError) {
        setError(hashErrorDescription || hashError);
        return;
      }

      // Determine where to redirect after session is established
      const isRecovery = hashType === 'recovery';
      const redirectTo = isRecovery ? '/auth/set-password' : '/crm';

      if (accessToken) {
        // Wait for Supabase to process the hash and establish session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          setError(sessionError.message);
          return;
        }

        if (session) {
          // Clear the hash from URL for cleaner appearance
          window.history.replaceState(null, '', window.location.pathname);
          navigate(redirectTo, { replace: true });
          return;
        }

        // If no session yet, wait a moment for Supabase to process
        setTimeout(async () => {
          const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession();
          if (retryError) {
            setError(retryError.message);
            return;
          }
          if (retrySession) {
            window.history.replaceState(null, '', window.location.pathname);
            navigate(redirectTo, { replace: true });
          } else {
            setError('Failed to establish session. Please try logging in again.');
          }
        }, 1000);
        return;
      }

      // No code or access_token - check for existing session
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate('/crm', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/15">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Authentication failed</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/login', { replace: true })}>
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
